/**
 * 共通定数。Firestore 名前空間、リージョン、分析軸定義など。
 * 仕様書:
 *   - Firestore 構成: docs/01_spec/spec_common.md C-3, spec_line_bot.md A-5, spec_mail_system.md B-2
 *   - 分析軸: docs/01_spec/spec_mail_system.md B-4.3
 */

// ─────────────────────────────────────────────
// インフラ定数
// ─────────────────────────────────────────────
export const FUNCTIONS_REGION = 'asia-northeast1' as const;
export const FIRESTORE_REGION = 'asia-northeast1' as const;

export const FIREBASE_PROJECTS = {
  dev: 'caud-goenhub-dev',
  prod: 'caud-goenhub',
} as const;

// ─────────────────────────────────────────────
// Claude モデル（仕様書執筆時点の想定 ID、実装時に疎通確認必須。
//   docs/01_spec/spec_common.md C-4.7 の動的フォールバック機構を参照）
// ─────────────────────────────────────────────
export const CLAUDE_MODELS = {
  sonnetDefault: 'claude-sonnet-4-6',
  opus: 'claude-opus-4-7',
  haiku: 'claude-haiku-4-5',
} as const;

export const CLAUDE_FALLBACK_CHAIN = [
  'claude-sonnet-4-6',
  'claude-sonnet-4-5',
  'claude-3-5-sonnet-latest',
] as const;

/**
 * LINE Bot のモデル・ルーティング戦略。settings/general.routingMode で制御。
 * - "haiku": 全 round で Haiku（最速、シンプルな問合せ向け）
 * - "sonnet": 全 round で Sonnet（高品質、低速、従来挙動）
 * - "router": round 0 を Haiku、それ以降の synthesis round を Sonnet（バランス型）
 * 未設定時は settings/general.claudeModel を使用（後方互換）。
 */
export const LINE_ROUTING_MODES = ['haiku', 'sonnet', 'router'] as const;
export type LineRoutingMode = (typeof LINE_ROUTING_MODES)[number];

// ─────────────────────────────────────────────
// ロール（v1 / レガシー — 後方互換のため残置）
// ─────────────────────────────────────────────
export const ROLES = ['owner', 'editor', 'viewer'] as const;
export type Role = (typeof ROLES)[number];

export const ROLE_RANK: Record<Role, number> = {
  owner: 3,
  editor: 2,
  viewer: 1,
};

export function hasMinimumRole(userRole: Role, minimum: Role): boolean {
  return ROLE_RANK[userRole] >= ROLE_RANK[minimum];
}

// ─────────────────────────────────────────────
// ユーザー管理 v2: 部署 × 権限レベル × isOwner
// 仕様書: docs/01_spec/spec_user_management.md
// ─────────────────────────────────────────────

/**
 * 部署 ID。`admin` / `sales` / `marketing` / `accounting` は初期値（systemフラグ=true、削除不可）。任意で追加可能。
 * `accounting`（経理）は 2026-10-26 に初期部署へ追加（本番では同じ ID のカスタム部署として運用していた）。
 */
export const SYSTEM_DEPARTMENTS = ['admin', 'sales', 'marketing', 'accounting'] as const;
export type SystemDepartmentId = (typeof SYSTEM_DEPARTMENTS)[number];
export type DepartmentId = string;

export const SYSTEM_DEPARTMENT_LABELS: Record<SystemDepartmentId, string> = {
  admin: '管理者',
  sales: '営業',
  marketing: 'マーケティング',
  accounting: '経理',
};

/** 権限レベル。3 階層固定。 */
export const LEVELS = ['leader', 'employee', 'external'] as const;
export type Level = (typeof LEVELS)[number];

export const LEVEL_LABELS: Record<Level, string> = {
  leader: 'リーダー',
  employee: '社員',
  external: '外部',
};

/** 単一の機能に対するアクション種別。 */
export const ACTIONS = ['view', 'create', 'edit', 'delete'] as const;
export type Action = (typeof ACTIONS)[number];

export const ACTION_LABELS: Record<Action, string> = {
  view: '閲覧',
  create: '新規作成',
  edit: '編集',
  delete: '削除',
};

/** 招待・アカウントのステータス。 */
export const ADMIN_STATUSES = ['pending', 'active'] as const;
export type AdminStatus = (typeof ADMIN_STATUSES)[number];

/**
 * 機能 1 つに対する 4 アクションの可否マップ。
 * 部分未設定（undefined）は「未設定 = false」と解釈する。
 */
export interface FeaturePermission {
  view?: boolean;
  create?: boolean;
  edit?: boolean;
  delete?: boolean;
}

/** featureId → アクション可否のマップ。 */
export type PermissionMap = Record<string, FeaturePermission>;

/**
 * 単一の機能（サイドバーのリーフメニュー = 1ページ、または親メニュー = 複数タブのハブ）。
 * actions は UI で表示するチェックボックスの対象。
 *
 * v3.3 IA刷新:
 * - 親メニュー (Hub) は `tabs` を持つ。Hub ページ自体は構造的ラッパーで
 *   独自の permission を持たず、サイドバー表示は「いずれかのタブに view 権限があれば表示」とする。
 * - 各タブも FeatureItem として permission を持つ（既存 featureId 互換）。
 */
export interface FeatureItem {
  /** featureId（PermissionMap のキーとして使用）。 */
  id: string;
  label: string;
  /** ルーティングパス。サイドバーリンクとアクセスガードに使う。 */
  path: string;
  /** lucide-react のアイコン名。 */
  icon: string;
  /** この機能で意味のあるアクション一覧。最低限 ['view'] は必須。 */
  actions: readonly Action[];
  /** 大項目アイコンの emoji 装飾（任意）。 */
  emoji?: string;
  /**
   * タブ子要素（親メニュー = Hub の場合のみ）。
   * 各タブは独自の featureId + path を持つ。Hub の path は通常タブ無し時のデフォルトタブにリダイレクト。
   */
  tabs?: readonly FeatureItem[];
  /**
   * 親メニュー (Hub) の場合の query key。タブ切替時に使う URL パラメータ名。
   * 既定: 'tab'
   */
  tabQueryKey?: string;
  /**
   * Hub 内タブの URL キー。タブ間で `id` (featureId) を共有するケースが
   * あるため、タブ識別子はこの tabKey で行う。
   * 例: 営業設定 4 タブが同一 featureId 'sales.settings' を共有しつつ
   * tabKey で URL を区別する。
   */
  tabKey?: string;
}

/** 大項目（サイドバーのセクション）。 */
export interface FeatureGroup {
  id: string;
  label: string;
  /** 表題横の emoji（任意）。 */
  emoji?: string;
  items: readonly FeatureItem[];
}

/**
 * 4 アクション全許可ショートカット（leader 初期値の組立てに利用）。
 */
export const ALL_ACTIONS_ALLOWED: Required<FeaturePermission> = {
  view: true,
  create: true,
  edit: true,
  delete: true,
};

/**
 * 単一機能に対して特定アクションが許可されているか判定する純粋関数。
 * - isOwner=true は常に許可（最高権限）
 * - 該当 featureId が permissions に存在しない場合は false
 * - view アクションが false の場合、他アクションは自動的に false（ページ優先ルール）
 */
export function hasPermission(args: {
  isOwner: boolean;
  permissions: PermissionMap | undefined | null;
  featureId: string;
  action: Action;
}): boolean {
  if (args.isOwner) return true;
  const perms = args.permissions;
  if (!perms) return false;
  const featurePerm = perms[args.featureId];
  if (!featurePerm) return false;
  // ページ優先: view が false / undefined なら他アクションも不可
  if (args.action !== 'view' && !featurePerm.view) return false;
  return Boolean(featurePerm[args.action]);
}

/**
 * 部署×レベルの組合せから rolePermissions ドキュメント ID を作成する。
 * カスタム部署 ID には `-` も許容するため、区切りは `__` を使う。
 */
export function makeRoleKey(dept: DepartmentId, level: Level): string {
  return `${dept}__${level}`;
}

// ─────────────────────────────────────────────
// LINE 系定数
// ─────────────────────────────────────────────
export const LINE_INQUIRY_TYPES = [
  'event',
  'archive_playback',
  'limited_contents',
  'service_info',
  'product_info',
] as const;
export type LineInquiryType = (typeof LINE_INQUIRY_TYPES)[number];

export const LINE_TOOL_NAMES = [
  'query_events',
  'query_archive_playback',
  'query_limited_contents',
  'query_service_info',
  'query_product_info',
  'switch_context',
  'end_conversation',
] as const;
export type LineToolName = (typeof LINE_TOOL_NAMES)[number];

/** 配信コード自動生成で使う文字セット（0/O/1/I を除外した 32 文字） */
export const DELIVERY_CODE_CHARSET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
export const DELIVERY_CODE_DEFAULT_LENGTH = 6;
export const DELIVERY_CODE_MAX_LENGTH = 20;
export const DELIVERY_CODE_PATTERN = /^[A-Z0-9]{6,20}$/;

/** セッション自動クローズまでの無応答時間（デフォルト） */
export const DEFAULT_SESSION_TIMEOUT_MIN = 30;

// ─────────────────────────────────────────────
// お問合せ自動化（Part C / spec_inquiry_automation.md）
// ─────────────────────────────────────────────

/** Vertex AI text-embedding-gecko-multilingual@001 の次元数。 */
export const VECTOR_EMBEDDING_DIMENSION = 768;

/** QA データソースのチャネル（LINE Bot / Public Web チャット / 両方）。 */
export const INQUIRY_CHANNELS = ['line', 'public', 'both'] as const;
export type InquiryChannel = (typeof INQUIRY_CHANNELS)[number];

/** Public Web チャットのセッション状態。 */
export const INQUIRY_SESSION_STATUSES = ['active', 'closed', 'escalated'] as const;
export type InquirySessionStatus = (typeof INQUIRY_SESSION_STATUSES)[number];

/** チャットメッセージの送信者種別。 */
export const CHAT_SENDER_TYPES = ['user', 'ai', 'admin', 'system'] as const;
export type ChatSenderType = (typeof CHAT_SENDER_TYPES)[number];

/** チャットメッセージのコンテンツ形式。 */
export const CHAT_MESSAGE_CONTENT_FORMATS = ['plain', 'markdown'] as const;
export type ChatMessageContentFormat = (typeof CHAT_MESSAGE_CONTENT_FORMATS)[number];

/** 応答主体の種別（AI 自動 / AI 下書きを事務送信 / 事務手書き / 事務上書き）。 */
export const INQUIRY_RESPONDER_TYPES = [
  'ai_auto',
  'ai_drafted_admin_sent',
  'admin_only',
  'admin_overwrite',
] as const;
export type InquiryResponderType = (typeof INQUIRY_RESPONDER_TYPES)[number];

/** エスカレーション状態。 */
export const ESCALATION_STATUSES = ['none', 'pending', 'resolved'] as const;
export type EscalationStatus = (typeof ESCALATION_STATUSES)[number];

/** 事務承認キューの状態。 */
export const INQUIRY_QUEUE_STATUSES = ['pending', 'in_progress', 'resolved', 'cancelled'] as const;
export type InquiryQueueStatus = (typeof INQUIRY_QUEUE_STATUSES)[number];

/** 事務承認キューでの最終処理種別。 */
export const INQUIRY_QUEUE_RESOLUTIONS = [
  'sent_as_is',
  'edited',
  'manual_rewrite',
  'cancelled',
] as const;
export type InquiryQueueResolution = (typeof INQUIRY_QUEUE_RESOLUTIONS)[number];

/** AI 信頼度のデフォルト閾値（Phase 2 初期は 0.85 推奨、運用後 0.7 へ緩和）。 */
export const DEFAULT_AI_CONFIDENCE_THRESHOLD = 0.7;
export const INITIAL_AI_CONFIDENCE_THRESHOLD = 0.85;

/** ベクトル類似検索の閾値。これ未満は「該当 QA なし」とみなす。 */
export const VECTOR_SIMILARITY_MIN_THRESHOLD = 0.5;
/** ベクトル類似検索のヒット閾値。これ以上は「ほぼ既存 QA で対応可能」とみなす。 */
export const VECTOR_SIMILARITY_HIT_THRESHOLD = 0.85;

// ─────────────────────────────────────────────
// Mail 系定数
// ─────────────────────────────────────────────
export const MAIL_STATUSES = ['draft', 'review', 'approved', 'sent'] as const;
export type MailStatus = (typeof MAIL_STATUSES)[number];

export const MAIL_SECTION_TYPES = ['greeting', 'lead', 'body', 'cta', 'closing', 'ps'] as const;
export type MailSectionType = (typeof MAIL_SECTION_TYPES)[number];

export const MAIL_LANGS = ['ja', 'en', 'zh', 'ko'] as const;
export type MailLang = (typeof MAIL_LANGS)[number];

export const FOOTER_SEPARATORS = ['line', 'double', 'dots', 'none'] as const;
export type FooterSeparator = (typeof FOOTER_SEPARATORS)[number];

/** 量子場解析の 8 軸（docs/01_spec/spec_mail_system.md B-4.3） */
export const ANALYSIS_AXES = [
  {
    id: 'target',
    name: '👤 ターゲット分析',
    desc: 'メールの受信者ペルソナと訴求ポイントの適合性を分析',
    category: '基本',
  },
  {
    id: 'value',
    name: '💎 価値提案分析',
    desc: '提供している価値の明確さと独自性を分析',
    category: '基本',
  },
  {
    id: 'emotion',
    name: '💫 感情体験分析',
    desc: '読者の感情の流れと心理的インパクトを分析',
    category: '基本',
  },
  {
    id: 'timing',
    name: '🕐 配信日時分析',
    desc: '最適な配信タイミングと頻度を提案',
    category: '基本',
  },
  {
    id: 'abtest',
    name: '🔬 件名A/Bテスト案',
    desc: '件名のバリエーションと各パターンの狙いを生成',
    category: '拡張',
  },
  {
    id: 'dropout',
    name: '📉 読了離脱ポイント分析',
    desc: '本文のどこで読者の関心が落ちるかを段落単位で分析',
    category: '拡張',
  },
  {
    id: 'cta_rate',
    name: '🎯 CTA到達率予測',
    desc: '本文の流れから CTA クリックに至る心理的導線を評価',
    category: '拡張',
  },
  {
    id: 'benchmark',
    name: '📊 業界ベンチマーク比較',
    desc: '同業種メールマーケティングの一般的傾向との比較',
    category: '拡張',
  },
] as const;

export type AnalysisAxisId = (typeof ANALYSIS_AXES)[number]['id'];

// ─────────────────────────────────────────────
// Rate Limiting（参考値、実装では settings で上書き可能）
// ─────────────────────────────────────────────
export const RATE_LIMITS = {
  lineMessagePerMin: 10,
  secretUpdatePerHour: 5,
  inviteSendPerHour: 10,
  mailGeneratePerMin: 20,
  mailAnalysisPerHour: 10,
  mailSendPerDay: 100,
  mailSendRecipientsMax: 50,
  mailTestSendPerDay: 50,
  reviewRequestPerMailPerHour: 5,
} as const;
